import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import { callAllHandlers } from "@smartsheet/lodestar-utils";
import { AllHTMLAttributes, HTMLAttributes, MouseEventHandler, Ref, useCallback } from "react";
import { noop } from "../../../../constants/noop";
import { MakeOptional } from "../../../../util/MakeOptional";
import { useFlyout, UseFlyoutOptions } from "../../../flyout";
import { DatePickerDialogFlyoutProps } from "../../types";
import { DatePickerDialogProps } from "../components/DatePickerDialog";

type HookProvidedProps = "targetElement";
type ApplicableFlyoutProps<TargetElement> = Pick<
    UseFlyoutOptions<TargetElement>,
    keyof Omit<DatePickerDialogFlyoutProps, HookProvidedProps> | "targetRef"
>;

export type UseDatePickerDialogOptions<TargetElementType extends HTMLElement = HTMLButtonElement> = Omit<
    MakeOptional<ApplicableFlyoutProps<TargetElementType> & DatePickerDialogProps, "id">,
    HookProvidedProps
> & {
    /**
     * Function that will be called when the flyout wants to close.
     * Not using the one baked into flyout, as we need to pass an argument
     */
    onCloseRequested: (refocusTarget: boolean) => void;

    /**
     * Function that will be called when the flyout wants to open.
     */
    onOpenRequested: () => void;

    /**
     * A ref that will be applied to the DatePicker container.
     */
    pickerRef?: Ref<HTMLDivElement>;

    /**
     * If true, the target element will be refocused when the picker closes.
     *
     * defaults to true.
     */
    refocusTargetOnClose?: boolean;
};

export type UseDatePickerDialogResult<TargetElementType extends HTMLElement = HTMLButtonElement> = {
    getPickerProps: (overrides?: Partial<DatePickerDialogProps>) => DatePickerDialogProps & {
        ref: Ref<HTMLDivElement>;
    };
    getTargetHTMLProps: (overrides?: AllHTMLAttributes<TargetElementType>) => HTMLAttributes<TargetElementType> & {
        ref: Ref<TargetElementType>;
    };
};

const defaultOffset = {
    distance: 4,
} as const;

/**
 * This component connects a DatePickerDialog and its target.
 * The target could be any focusable element.
 */
export const useDatePickerDialog = <TargetElementType extends HTMLElement = HTMLButtonElement>(
    options: UseDatePickerDialogOptions<TargetElementType>
): UseDatePickerDialogResult<TargetElementType> => {
    const {
        applyImmediately,
        className,
        clientId,
        dayIsDisabled,
        id,
        initialPickerDate,
        isOpen,
        locale,
        maxDate,
        minDate,
        offset = defaultOffset,
        onChange: providedOnChange,
        onClear: providedOnClear,
        onCloseRequested: providedOnCloseRequested,
        onOpenRequested,
        pickerRef: providedPickerRef,
        placement = "bottom",
        refocusTargetOnClose = true,
        targetRef: providedTargetRef = null,
        value,
        ...rest
    } = options;

    const dialogProps = {
        applyImmediately,
        className,
        clientId,
        dayIsDisabled,
        id,
        initialPickerDate,
        locale,
        maxDate,
        minDate,
        value,
    };

    const pickerRef = useEnsuredForwardedRef(providedPickerRef);
    const targetRef = useEnsuredForwardedRef(providedTargetRef);

    const { targetProps, flyoutProps } = useFlyout<TargetElementType>({
        isOpen,
        // We're going to ignore this for now. We'll deal with it manually in here.
        onCloseRequested: noop,
        placement,
        offset,
        // We need to handle this manually in the input since the element we need to
        // refocus is separate from the element we point at.
        closeOnEscape: false,
        id,
        targetRef,
        ...rest,
    });

    const focusTarget = useCallback(() => {
        requestAnimationFrame(() => {
            targetRef.current?.focus();
        });
    }, [targetRef]);

    /**
     * @param refocusTarget if true, manually focus the target again. We cannot rely on
     *                      Flyout here since we force the target ref to be the input.
     */
    const onCloseRequested = useCallback(
        (refocusTarget: boolean) => {
            providedOnCloseRequested(refocusTarget);
            if (refocusTarget && refocusTargetOnClose) {
                focusTarget();
            }
        },
        [providedOnCloseRequested, refocusTargetOnClose, focusTarget]
    );

    const onChange: UseDatePickerDialogOptions["onChange"] = useCallback(
        (newDate) => {
            providedOnChange(newDate);
            if (refocusTargetOnClose) {
                focusTarget();
            }
        },
        [providedOnChange, refocusTargetOnClose, focusTarget]
    );

    const onClear = useCallback(() => {
        providedOnClear?.();
        if (refocusTargetOnClose) {
            focusTarget();
        }
    }, [providedOnClear, refocusTargetOnClose, focusTarget]);

    const onTargetClick: MouseEventHandler<TargetElementType> = useCallback(() => {
        if (!isOpen) {
            onOpenRequested();
        } else {
            onCloseRequested(true);
        }
    }, [isOpen, onCloseRequested, onOpenRequested]);

    const getPickerProps: UseDatePickerDialogResult["getPickerProps"] = (overrides) => {
        return {
            ...dialogProps,
            ...flyoutProps,
            ...overrides,
            onChange,
            onClear,
            // We're going to override the onCloseRequested from useFlyout here
            // since the picker dialogs need to do a little more work to focus
            // their calendar buttons.
            onCloseRequested,
            ref: pickerRef,
        };
    };

    const getTargetHTMLProps = useCallback<UseDatePickerDialogResult<TargetElementType>["getTargetHTMLProps"]>(
        ({ onClick, ...overrides } = {}) => {
            return {
                ...targetProps,
                ...overrides,
                onClick: callAllHandlers(onClick, onTargetClick),
            };
        },
        [onTargetClick, targetProps]
    );

    return {
        getPickerProps,
        getTargetHTMLProps,
    };
};
